import { useEffect, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useOrders } from '../../hooks/useOrders';
import {
  Users as UsersIcon,
  Plus,
  Search,
  Edit2,
  Trash2,
  Shield,
  CheckCircle,
  XCircle,
  ToggleLeft,
  ToggleRight,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Phone,
  Mail,
  Calendar,
  Store,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUsers } from '../../hooks/useUsers';
import { useBranches } from '../../hooks/useBranches';
import { usePageTitle } from '../../hooks/usePageTitle';
import { User, UserRole, CreateUserDto, UpdateUserDto } from '../../services/users.service';
import { branchesService, Branch, CreateBranchDto } from '../../services/branches.service';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import { toast } from 'sonner';

interface UsersManagementPageProps {
  onNavigate: (page: string) => void;
}

const ROLES: { value: UserRole; label: string; color: string }[] = [
  { value: 'ADMIN',   label: 'Admin',   color: 'purple' },
  { value: 'MANAGER', label: 'Manager', color: 'blue'   },
  { value: 'CHEF',    label: 'Chef',    color: 'orange' },
  { value: 'STAFF',   label: 'Staff',   color: 'green'  },
  { value: 'VIEWER',  label: 'Viewer',  color: 'gray'   },
];

function roleBadge(role: string) {
  const r = ROLES.find((x) => x.value === role?.toUpperCase());
  const color = r?.color ?? 'gray';
  const label = r?.label ?? role;
  const map: Record<string, string> = {
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    blue:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    green:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    gray:   'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[color] ?? map.gray}`}>
      {label}
    </span>
  );
}

function UserAvatar({ user, size = 'md' }: { user: User; size?: 'sm' | 'md' | 'lg' }) {
  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm';
  if (user.avatar) {
    return <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} className={`${sz} rounded-full object-cover`} />;
  }
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center font-bold text-white flex-shrink-0`}>
      {initials}
    </div>
  );
}

export function UsersManagementPage({ onNavigate }: UsersManagementPageProps) {
  usePageTitle('Users');

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const { user: authUser } = useAuth();
  const { orders: ordersData, stats: ordersStats } = useOrders({ page: 1, limit: 50 });

  const isPlatformAdmin = Boolean(authUser?.permissions?.includes('tenants_platform'));

  const { users, total, loading, submitting, error, createUser, updateUser, deleteUser, toggleStatus, refresh } = useUsers({ page: 1, limit: 100 });
  
  // Fetch real branches from database
  const { branches: realBranches, loading: branchesLoading, refresh: refreshBranches } = useBranches({ 
    tenantId: isPlatformAdmin ? undefined : authUser?.restaurantId,
    isActive: true 
  });

  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterRole,   setFilterRole]   = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);
  const [editingUser,     setEditingUser]     = useState<User | null>(null);
  const [deletingUserId,  setDeletingUserId]  = useState<string | null>(null);
  const [branchForm, setBranchForm] = useState<CreateBranchDto>({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    manager: '',
    isActive: true,
  });
  const [branchSubmitting, setBranchSubmitting] = useState(false);
  const [branchFormError, setBranchFormError] = useState<string | null>(null);
  // ── Client-side filter ──────────────────────────────────────────────────
  const filtered = users.filter((u) => {
    const q = searchQuery.toLowerCase();
    const name = `${u.firstName} ${u.lastName}`.toLowerCase();
    const matchSearch = !q || name.includes(q) || u.email.toLowerCase().includes(q);
    const matchRole   = filterRole   === 'all' || u.role?.toUpperCase() === filterRole;
    const matchStatus = filterStatus === 'all'
      || (filterStatus === 'active'   && u.isActive)
      || (filterStatus === 'inactive' && !u.isActive);
    return matchSearch && matchRole && matchStatus;
  });

  // ── Stats (computed from full fetched list) ───────────────────────────
  const activeCount   = users.filter((u) => u.isActive).length;
  const inactiveCount = users.filter((u) => !u.isActive).length;
  const adminCount    = users.filter((u) => u.role?.toUpperCase() === 'ADMIN').length;

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleCreate = async (data: CreateUserDto) => {
    try {
      await createUser(data);
      setShowCreateModal(false);
      toast.success('User created successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create user');
    }
  };

  const handleUpdate = async (id: string, data: UpdateUserDto) => {
    try {
      await updateUser(id, data);
      setEditingUser(null);
      toast.success('User updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update user');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id);
      setDeletingUserId(null);
      toast.success('User deleted successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await toggleStatus(id);
      toast.success('User status updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle status');
    }
  };

  const canManageBranches = Boolean(
    authUser?.permissions?.includes('branches_admin') ||
    authUser?.role?.toUpperCase() === 'ADMIN' ||
    authUser?.role?.toUpperCase() === 'SUPER_ADMIN'
  );

  const handleCreateBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    setBranchFormError(null);

    if (!branchForm.name?.trim()) {
      setBranchFormError('Branch name is required');
      return;
    }

    if (!branchForm.code?.trim()) {
      setBranchFormError('Branch code is required');
      return;
    }

    setBranchSubmitting(true);
    try {
      await branchesService.createBranch({
        ...branchForm,
        name: branchForm.name.trim(),
        code: branchForm.code.trim(),
        address: branchForm.address?.trim() || undefined,
        phone: branchForm.phone?.trim() || undefined,
        email: branchForm.email?.trim() || undefined,
        manager: branchForm.manager?.trim() || undefined,
      });

      toast.success('Branch created successfully');
      setShowCreateBranchModal(false);
      setBranchForm({ name: '', code: '', address: '', phone: '', email: '', manager: '', isActive: true });
      refreshBranches();
    } catch (err: any) {
      setBranchFormError(err?.message || 'Failed to create branch');
      toast.error(err?.message || 'Failed to create branch');
    } finally {
      setBranchSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePage="users"
        onNavigate={onNavigate}
        ordersCount={ordersStats?.total || ordersData.length}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <UsersIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {total} user{total !== 1 ? 's' : ''} {isPlatformAdmin ? 'across all tenants' : 'in your organisation'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canManageBranches && (
                  <button
                    onClick={() => setShowCreateBranchModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    <Store className="w-4 h-4" />
                    <span className="hidden sm:inline">New Branch</span>
                  </button>
                )}
                <button
                  onClick={refresh}
                  disabled={loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => onNavigate('rbac')}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                  title="Manage Roles & Permissions (Access Control)"
                >
                  <Shield className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New User</span>
                </button>
              </div>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Users',  value: total,         icon: UsersIcon,     bg: 'bg-indigo-50 dark:bg-indigo-900/30', ic: 'text-indigo-600 dark:text-indigo-400' },
                { label: 'Active',       value: activeCount,   icon: CheckCircle,   bg: 'bg-green-50 dark:bg-green-900/30',   ic: 'text-green-600 dark:text-green-400' },
                { label: 'Inactive',     value: inactiveCount, icon: XCircle,       bg: 'bg-red-50 dark:bg-red-900/30',       ic: 'text-red-600 dark:text-red-400' },
                { label: 'Admins',       value: adminCount,    icon: Shield,        bg: 'bg-purple-50 dark:bg-purple-900/30', ic: 'text-purple-600 dark:text-purple-400' },
              ].map((card) => (
                <div key={card.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3">
                  <div className={`p-2.5 rounded-lg ${card.bg}`}>
                    <card.icon className={`w-5 h-5 ${card.ic}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Roles</option>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {canManageBranches && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Branch Management</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Create branches for the active tenant, then map users to branches in the user form.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {branchesLoading ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Loading branches...</span>
                    ) : realBranches.length === 0 ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">No branches found</span>
                    ) : (
                      realBranches.map((branch) => (
                        <span
                          key={branch.id}
                          className="px-2.5 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                        >
                          {branch.name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <span className="ml-3 text-gray-500 dark:text-gray-400">Loading users…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16">
                  <UsersIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No users found</p>
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        {['User', 'Role', ...(isPlatformAdmin ? ['Tenant'] : []), 'Branch', 'Status', 'Last Login', 'Actions'].map((h) => (
                          <th key={h} className={`px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {filtered.map((user) => {
                        const isCurrentUser = user.id === authUser?.id;
                        return (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            {/* User */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <UserAvatar user={user} />
                                <div>
                                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                                    {user.firstName} {user.lastName}
                                    {isCurrentUser && (
                                      <span className="ml-2 text-xs text-indigo-500 dark:text-indigo-400 font-normal">(you)</span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                    <Mail className="w-3 h-3" />{user.email}
                                  </p>
                                  {user.phone && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                      <Phone className="w-3 h-3" />{user.phone}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            {/* Role */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              {roleBadge(user.role)}
                            </td>
                            {/* Tenant — visible to platform admins only */}
                            {isPlatformAdmin && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                {user.tenant?.name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                              </td>
                            )}
                            {/* Branch */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              {user.branch?.name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                            </td>
                            {/* Status */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              {user.isActive ? (
                                <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 text-sm font-semibold">
                                  <CheckCircle className="w-4 h-4" /> Active
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-sm font-semibold">
                                  <XCircle className="w-4 h-4" /> Inactive
                                </span>
                              )}
                            </td>
                            {/* Last Login */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.lastLogin ? (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {new Date(user.lastLogin).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">Never</span>
                              )}
                            </td>
                            {/* Actions */}
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleToggleStatus(user.id)}
                                  disabled={isCurrentUser}
                                  className="p-2 rounded-lg text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={user.isActive ? 'Deactivate' : 'Activate'}
                                >
                                  {user.isActive
                                    ? <ToggleRight className="w-4 h-4 text-green-500" />
                                    : <ToggleLeft  className="w-4 h-4 text-gray-400" />
                                  }
                                </button>
                                <button
                                  onClick={() => setEditingUser(user)}
                                  className="p-2 rounded-lg text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                  title="Edit user"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeletingUserId(user.id)}
                                  disabled={isCurrentUser}
                                  className="p-2 rounded-lg text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={isCurrentUser ? "Can't delete yourself" : 'Delete user'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <UserFormModal
          mode="create"
          branches={realBranches}
          tenantId={authUser?.restaurantId ?? ''}
          submitting={submitting}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => handleCreate(data as CreateUserDto)}
        />
      )}

      {/* Edit Modal */}
      {editingUser && (
        <UserFormModal
          mode="edit"
          user={editingUser}
          branches={realBranches}
          tenantId={authUser?.restaurantId ?? ''}
          submitting={submitting}
          onClose={() => setEditingUser(null)}
          onSubmit={(data) => handleUpdate(editingUser.id, data as UpdateUserDto)}
        />
      )}

      {/* Delete Confirm */}
      {deletingUserId && (
        <DeleteConfirmModal
          userName={(() => {
            const u = users.find((x) => x.id === deletingUserId);
            return u ? `${u.firstName} ${u.lastName}` : '';
          })()}
          submitting={submitting}
          onConfirm={() => handleDelete(deletingUserId)}
          onCancel={() => setDeletingUserId(null)}
        />
      )}

      {showCreateBranchModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create New Branch</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This branch will be created under the currently active tenant.</p>
              </div>
              <button onClick={() => setShowCreateBranchModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleCreateBranch} className="p-6 space-y-4">
              {branchFormError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {branchFormError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Branch Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={branchForm.name || ''}
                    onChange={(e) => setBranchForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Downtown Branch"
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={branchForm.code || ''}
                    onChange={(e) => setBranchForm((p) => ({ ...p, code: e.target.value }))}
                    placeholder="DT-001"
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Address</label>
                <input
                  type="text"
                  value={branchForm.address || ''}
                  onChange={(e) => setBranchForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Nairobi CBD"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Phone</label>
                  <input
                    type="text"
                    value={branchForm.phone || ''}
                    onChange={(e) => setBranchForm((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+254..."
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={branchForm.email || ''}
                    onChange={(e) => setBranchForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="branch@tenant.com"
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Manager</label>
                <input
                  type="text"
                  value={branchForm.manager || ''}
                  onChange={(e) => setBranchForm((p) => ({ ...p, manager: e.target.value }))}
                  placeholder="Branch manager name"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateBranchModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={branchSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg transition-colors"
                >
                  {branchSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Branch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── UserFormModal ────────────────────────────────────────────────────────────

interface UserFormModalProps {
  mode: 'create' | 'edit';
  user?: User;
  branches: Branch[];
  tenantId: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserDto | UpdateUserDto) => Promise<void>;
}

function UserFormModal({ mode, user, branches, tenantId, submitting, onClose, onSubmit }: UserFormModalProps) {
  const selectedTenantId = mode === 'edit' ? (user?.tenantId ?? tenantId) : tenantId;
  const selectedTenantName = mode === 'edit' ? (user?.tenant?.name || user?.tenantId || 'Unknown tenant') : null;
  const filteredBranches = branches.filter((branch) => branch.tenantId === selectedTenantId);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [customerTypesLoading, setCustomerTypesLoading] = useState(false);

  const [form, setForm] = useState({
    firstName: user?.firstName ?? '',
    lastName:  user?.lastName  ?? '',
    email:     user?.email     ?? '',
    password:  '',
    confirmPassword: '',
    allowedCustomerTypeIds: user?.allowedCustomerTypeIds ?? [],
    phone:     user?.phone     ?? '',
    role:      (user?.role     ?? 'STAFF') as UserRole,
    branchId:  user?.branchId  ?? (filteredBranches.length > 0 ? filteredBranches[0].id : ''),
    isActive:  user?.isActive  ?? true,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [restrictToCustomerTypes, setRestrictToCustomerTypes] = useState(
    (user?.allowedCustomerTypeIds?.length ?? 0) > 0,
  );

  useEffect(() => {
    if (!selectedTenantId) {
      setCustomerTypes([]);
      setCustomerTypesLoading(false);
      return;
    }

    setCustomerTypesLoading(true);
    customerTypesService
      .getAll(selectedTenantId)
      .then((res) => {
        if (res.success && res.data) {
          setCustomerTypes(res.data.filter((ct) => ct.isActive));
        } else {
          setCustomerTypes([]);
        }
      })
      .catch(() => setCustomerTypes([]))
      .finally(() => setCustomerTypesLoading(false));
  }, [selectedTenantId]);

  const canRestrictToCustomerTypes = customerTypes.length > 0;

  useEffect(() => {
    if (!customerTypesLoading && !canRestrictToCustomerTypes) {
      setRestrictToCustomerTypes(false);
      setForm((prev) => ({ ...prev, allowedCustomerTypeIds: [] }));
    }
  }, [customerTypesLoading, canRestrictToCustomerTypes]);

  const set = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.firstName.trim()) return setFormError('First name is required');
    if (!form.lastName.trim())  return setFormError('Last name is required');
    if (!form.email.trim())     return setFormError('Email is required');
    if (mode === 'create') {
      if (!form.password || form.password.length < 8)
        return setFormError('Password must be at least 8 characters');
      if (!form.confirmPassword)
        return setFormError('Please confirm the password');
      if (form.password !== form.confirmPassword)
        return setFormError('Password and confirm password do not match');
    } else if (form.password || form.confirmPassword) {
      if (!form.password || form.password.length < 8)
        return setFormError('New password must be at least 8 characters');
      if (!form.confirmPassword)
        return setFormError('Please confirm the new password');
      if (form.password !== form.confirmPassword)
        return setFormError('New password and confirm password do not match');
    }
    if (restrictToCustomerTypes && form.allowedCustomerTypeIds.length === 0) {
      return setFormError('Select at least one customer type or disable customer type restriction');
    }

    try {
      if (mode === 'create') {
        await onSubmit({
          email:     form.email,
          password:  form.password,
          firstName: form.firstName,
          lastName:  form.lastName,
          role:      form.role,
          allowedCustomerTypeIds: restrictToCustomerTypes ? form.allowedCustomerTypeIds : [],
          phone:     form.phone || undefined,
          branchId:  form.branchId || undefined,
          tenantId,
          isActive:  form.isActive,
        } as CreateUserDto);
      } else {
        await onSubmit({
          firstName: form.firstName,
          lastName:  form.lastName,
          email:     form.email,
          password:  form.password || undefined,
          allowedCustomerTypeIds: restrictToCustomerTypes ? form.allowedCustomerTypeIds : [],
          role:      form.role,
          phone:     form.phone || undefined,
          branchId:  form.branchId || undefined,
          isActive:  form.isActive,
        } as UpdateUserDto);
      }
    } catch (err: any) {
      setFormError(err.message || 'Something went wrong');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {mode === 'create' ? 'Create New User' : 'Edit User'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {mode === 'create' ? 'Add a new user to your organisation' : 'Update user details and permissions'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {formError}
            </div>
          )}

          {/* Name row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                placeholder="John"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                placeholder="Doe"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          {/* Contact row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="john.doe@example.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Phone (optional)</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+1 234 567 8900"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Password — create only */}
          {mode === 'create' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => set('confirmPassword', e.target.value)}
                    placeholder="Re-enter password"
                    className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Password reset — edit only */}
          {mode === 'edit' && (
            <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Change Password</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Optional. Leave blank to keep the current password.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => set('password', e.target.value)}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label={showPassword ? 'Hide new password' : 'Show new password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={form.confirmPassword}
                      onChange={(e) => set('confirmPassword', e.target.value)}
                      placeholder="Re-enter new password"
                      className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label={showConfirmPassword ? 'Hide confirm new password' : 'Show confirm new password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tenant + Branch + Role */}
          <div className={mode === 'edit' ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-3'}>
            {mode === 'edit' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Tenant
                </label>
                <input
                  type="text"
                  value={selectedTenantName ?? ''}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 rounded-lg"
                  disabled
                  readOnly
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Branch
              </label>
              <select
                value={form.branchId}
                onChange={(e) => set('branchId', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">No branch</option>
                {filteredBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={form.role}
                onChange={(e) => set('role', e.target.value as UserRole)}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {/* Customer Type Access */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Customer Type Access</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Restrict this user to only selected customer types.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (canRestrictToCustomerTypes) {
                    setRestrictToCustomerTypes((prev) => !prev);
                  }
                }}
                disabled={!canRestrictToCustomerTypes || customerTypesLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${restrictToCustomerTypes ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'} ${(!canRestrictToCustomerTypes || customerTypesLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${restrictToCustomerTypes ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {!customerTypesLoading && !canRestrictToCustomerTypes && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Customer type restriction is unavailable until at least one active customer type exists for this tenant.
              </p>
            )}

            {restrictToCustomerTypes && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {
                  customerTypes.map((ct) => {
                    const checked = form.allowedCustomerTypeIds.includes(ct.id);
                    return (
                      <label
                        key={ct.id}
                        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              set('allowedCustomerTypeIds', [...form.allowedCustomerTypeIds, ct.id]);
                            } else {
                              set('allowedCustomerTypeIds', form.allowedCustomerTypeIds.filter((id) => id !== ct.id));
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>{ct.name}</span>
                      </label>
                    );
                  })
                }
              </div>
            )}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Active Status</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">User can log in when active</p>
            </div>
            <button
              type="button"
              onClick={() => set('isActive', !form.isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── DeleteConfirmModal ───────────────────────────────────────────────────────

interface DeleteConfirmModalProps {
  userName: string;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmModal({ userName, submitting, onConfirm, onCancel }: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Delete User</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Are you sure you want to permanently delete{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{userName}</span>?
          This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}